import { useQuery } from '@apollo/client/react'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'

/**
 * Lightweight count used solely to gate the "Create a post" task's pending →
 * complete transition. We deliberately keep this separate from the Up Next
 * list query (which only surfaces `scheduled` posts): a post the user
 * scheduled may since have been sent, so gating on `scheduled` alone left
 * users who had already published unable to complete the task (CORE-5626).
 *
 * Any post in `scheduled`, `sending`, or `sent` means the user has created a
 * post, so the task should be completable.
 */
export const GetCreatedPostCount = graphql(`
  query GetCreatedPostCount($organizationId: OrganizationId!) {
    postCounts(
      input: {
        organizationId: $organizationId
        filter: { status: [scheduled, sending, sent] }
      }
    ) {
      counts {
        channelId
        count
      }
    }
  }
`)

type UseHasCreatedPostResult = {
  hasCreatedPost: boolean
  loading: boolean
}

export const useHasCreatedPost = (): UseHasCreatedPostResult => {
  const organizationId = useOrganizationId()

  const { data, loading } = useQuery(GetCreatedPostCount, {
    variables: { organizationId: organizationId ?? '' },
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  })

  const totalCount =
    data?.postCounts?.counts?.reduce((sum, { count }) => sum + count, 0) ?? 0

  return { hasCreatedPost: totalCount > 0, loading }
}
